import { useState, useRef, useCallback } from 'react'
import { toast } from 'react-toastify'
import { addObject, editObjectFilter, getObjectsWithFilter } from '../../api/apiClientCrud'
import { ENTITY_LABELS } from '../../utils/constants/misc'

const getErrorMessage = (error) => error?.response?.statusText || error?.message || 'Une erreur est survenue'

const useSaveEntity = ({
  entityName,
  mode,
  id,
  user,
  editFn,
  settingsKey,
  updateCounter = false,
  counterName,
  contractLineName,
  onSuccess = () => {},
  onError = () => {},
  onSuccessRedirect = () => {},
  buildPayload
}) => {
  const toastId = useRef(null)
  const [loading, setLoading] = useState(false)

  const saveEntity = useCallback(
    async ({ data, extra = {} }) => {
      setLoading(true)
      if (!toastId.current) {
        toastId.current = toast.loading('Enregistrement en cours…')
      }

      const isEdit = mode === 'edit'

      try {
        if (!isEdit) {
          let settings = {}
          let counters = {}
          let currentNb = null

          if (updateCounter) {
            // Fetch counters
            const countersRes = await getObjectsWithFilter('counters', {
              company_id: user.company_id
            })
            counters = countersRes.data.counters?.[0] ?? {}

            // Check object limit
            let objectLimit = user.is_superAdmin ? 0 : 5
            const contract = user.company?.contracts?.[0]
            if (contract && !user.is_superAdmin) {
              const contractLine = contract.lines?.find((line) => line.module === contractLineName)
              if (contractLine) objectLimit = contractLine.quantity
            }

            if (objectLimit !== 0 && counters[counterName] >= objectLimit) {
              throw new Error(`Compteur dépassé pour ${entityName}`)
            }
          }

          // Fetch settings
          const settingsRes = await getObjectsWithFilter('settings', {
            company_id: user.company_id
          })
          settings = settingsRes.data
          currentNb = parseInt(settings?.[settingsKey] ?? 0, 10)

          // Build payload
          const payload = buildPayload({
            data,
            user,
            mode,
            id,
            settings,
            counterValue: currentNb,
            ...extra
          })

          // Save entity
          const addRes = await addObject(entityName, payload)
          const newId = addRes.data.id
          if (settingsKey)
            await editObjectFilter('settings', { company_id: user.company_id }, { [settingsKey]: currentNb + 1 })
          // Update settings & counters if needed
          if (updateCounter) {
            await editObjectFilter(
              'counters',
              { company_id: user.company_id },
              { [counterName]: counters[counterName] + 1 }
            )
          }

          toast.update(toastId.current, {
            type: 'success',
            render: `${ENTITY_LABELS[entityName] || entityName}${
              addRes.data[settingsKey] ? ` ${addRes.data[settingsKey]}` : ''
            } enregistré avec succès`,
            autoClose: 2000,
            isLoading: false
          })
          toastId.current = null
          onSuccess(addRes)
          onSuccessRedirect(newId)
        } else {
          //EDIT
          const payload = buildPayload({ data, user, mode, id, ...extra })
          const res = await editFn(entityName, id, payload)
          toast.update(toastId.current, {
            type: 'success',
            render: 'Enregistrement terminé',
            autoClose: 2000,
            isLoading: false
          })
          onSuccess(res)
          onSuccessRedirect(id)
        }
      } catch (error) {
        toast.update(toastId.current, {
          type: 'error',
          render: getErrorMessage(error),
          autoClose: 2000,
          isLoading: false
        })
        onError(error)
      } finally {
        setLoading(false)
      }
    },
    [
      mode,
      entityName,
      id,
      user,
      editFn,
      settingsKey,
      updateCounter,
      counterName,
      contractLineName,
      buildPayload,
      onSuccess,
      onError,
      onSuccessRedirect
    ]
  )

  return { saveEntity, loading }
}

export default useSaveEntity
